/**
 * Type-aware section gating — shared by in-app viewer, TOC, and DOCX export.
 *
 * A section renders only when it belongs to the active report type's
 * canonical registry (plus custom-brief Q ids) AND passes visibility.
 */
import { getSectionsForType } from '../config/reportSections';
import type { ReportTemplate } from '../types/reportTemplate';
import type { SiteRagReport } from '../types/siteRag';
import type { GridLedStandbyPlanningReportInput } from './generationImpact/gridLedStandbyPlanningReportContent';
import { isCustomBriefQuestionSectionId } from './customBriefSections';
import {
  resolveSectionVisibility,
  type EffectiveTemplateConfig,
} from './effectiveTemplateConfig';

function shouldUseLiveTemplateLayout(
  report: Pick<SiteRagReport, 'templateId'>,
  template: ReportTemplate | null | undefined,
): boolean {
  if (!template) return false;
  if (report.templateId) return report.templateId === template.id;
  return true;
}

function resolveLiveSectionVisibility(
  sectionId: string,
  report: Pick<SiteRagReport, 'sectionVisibility'>,
  templateConfig: EffectiveTemplateConfig | null | undefined,
  liveLayout: boolean,
): boolean {
  if (liveLayout && templateConfig) {
    const tmplLevel = templateConfig.visibility[sectionId];
    if (typeof tmplLevel === 'boolean') return tmplLevel;
    return true;
  }
  return resolveSectionVisibility(sectionId, report.sectionVisibility, templateConfig);
}

/** True when `sectionId` belongs on a report of the given type. */
export function isSectionApplicableForReportType(
  sectionId: string,
  report: Pick<SiteRagReport, 'reportType' | 'sectionOrder'> & GridLedStandbyPlanningReportInput,
): boolean {
  if (report.reportType === 'custom-brief' && sectionId === 'projectContext') {
    return true;
  }
  if (report.reportType === 'custom-brief' && isCustomBriefQuestionSectionId(sectionId)) {
    const order = report.sectionOrder;
    if (order && order.length > 0 && !order.includes(sectionId)) return false;
    return true;
  }
  if (report.reportType === 'custom-brief' && sectionId === 'customBriefQuestions') {
    const order = report.sectionOrder ?? [];
    if (order.some(isCustomBriefQuestionSectionId)) return false;
    return order.includes('customBriefQuestions');
  }
  return getSectionsForType(report.reportType, report).some(s => s.sectionId === sectionId);
}

export function isSectionEnabledForType(
  sectionId: string,
  report: Pick<SiteRagReport, 'reportType' | 'sectionVisibility' | 'sectionOrder' | 'templateId'> & GridLedStandbyPlanningReportInput,
  templateConfig?: EffectiveTemplateConfig | null,
  template?: ReportTemplate | null,
): boolean {
  if (!isSectionApplicableForReportType(sectionId, report)) return false;

  const liveLayout = shouldUseLiveTemplateLayout(report, template);
  return resolveLiveSectionVisibility(sectionId, report, templateConfig, liveLayout);
}
